'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useState } from 'react';
import { AppToaster } from '@/components/ui/Toast';
import { ThemeSync } from '@/lib/theme';

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const message =
    error instanceof Error ? error.message : 'An unexpected error occurred';

  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-foreground"
    >
      <h1 className="text-2xl font-semibold">Something went wrong</h1>
      <p className="max-w-md text-center text-sm text-foreground/60">
        {message}
      </p>
      <button
        onClick={resetErrorBoundary}
        className="rounded-md bg-foreground px-4 py-2 text-sm text-background transition-colors hover:bg-foreground/80"
      >
        Try again
      </button>
    </div>
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            retry: 1,
          },
        },
      }),
  );

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        {children}
        <ThemeSync />
        <AppToaster />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
